import {
  calculators,
  enums,
  formatters,
  type parsers,
  productMatching,
  type selectors,
} from "@fraction/shared";

/**
 * A rate as it is shown on a screen: the STATED rate (what the borrower is quoted) next to the stored
 * effective rate money runs on. Plain numbers so web and mobile render it without Decimal.
 *
 * Built from `selectors.statedRate.selectStatedRate` for a deal, or from an offer's
 * `productMatching.getEffectivePricing` — never re-derived here.
 */
export interface DisplayedRate {
  /** The rate to print, as a decimal (0.0528). */
  rateDecimal: number;
  /** The stored effective annual rate (0.0541) — sort and compare on this, never on `rateDecimal`. */
  effectiveRateDecimal: number;
  /** The compounding `rateDecimal` names, or null when it is printed as stored. */
  compounding: "daily" | "monthly" | null;
}

/** A deal's {@link selectors.statedRate.StatedRate} as a {@link DisplayedRate}. */
export function toDisplayedRate(
  stated: selectors.statedRate.StatedRate | null | undefined
): DisplayedRate | undefined {
  if (!stated) {
    return undefined;
  }
  return {
    rateDecimal: stated.rate.toNumber(),
    effectiveRateDecimal: stated.storedRate.toNumber(),
    compounding: stated.compounding,
  };
}

/**
 * The rate an offer quotes on a deal stamped with `statedRateBasis`. `getEffectivePricing` always
 * states the nominal rate; a deal still on the EFFECTIVE_ANNUAL basis (every pre-switch deal) keeps
 * quoting the effective rate so its offers match its documents. A missing basis reads as
 * EFFECTIVE_ANNUAL, like `selectStatedRate`; surfaces with no deal pass NOMINAL.
 *
 * `base` picks the computed rate before any employee override (`baseRateDecimal`).
 */
export function selectOfferDisplayedRate(
  pricing: Pick<
    productMatching.EffectivePricing,
    "rateDecimal" | "statedRateDecimal" | "baseRateDecimal" | "baseStatedRateDecimal" | "statedCompounding"
  >,
  statedRateBasis: enums.StatedRateBasis | null | undefined,
  { base = false }: { base?: boolean } = {}
): DisplayedRate | undefined {
  const effective = base ? pricing.baseRateDecimal : pricing.rateDecimal;
  const stated = base ? pricing.baseStatedRateDecimal : pricing.statedRateDecimal;
  if (effective == null) {
    return undefined;
  }
  if (statedRateBasis !== enums.StatedRateBasis.NOMINAL || stated == null) {
    return { rateDecimal: effective, effectiveRateDecimal: effective, compounding: null };
  }
  return { rateDecimal: stated, effectiveRateDecimal: effective, compounding: pricing.statedCompounding };
}

/**
 * A product's stored rate (a Rate row's `minimumRate`) as it is published — always NOMINAL, since a
 * product quote has no deal to be stamped: the stated rate for the product's compounding for an
 * effective-rate lender (Fraction), the stored rate as-is otherwise.
 */
export function selectProductDisplayedRate(
  product: productMatching.StatedRateProductSources | null | undefined,
  storedRate: number | string | null | undefined
): DisplayedRate | undefined {
  if (storedRate == null || storedRate === "") {
    return undefined;
  }
  const effective = Number(storedRate);
  const periods = productMatching.selectProductStatedRatePeriods(product);
  return {
    rateDecimal: periods ? calculators.rates.toDisplayedStatedRate(effective, periods).toNumber() : effective,
    effectiveRateDecimal: effective,
    compounding: productMatching.statedCompoundingLabel(periods),
  };
}

type PublishedRate = Pick<parsers.rate.Rate, "minimumRate" | "productType" | "type" | "lenderId">;

/**
 * The product fields a published rate row (`parsers.rate.Rate`, as `useRates` returns) carries. The
 * row has no product, so its compounding falls back to the product type's default
 * (`resolveStatedCompoundFrequency`) — the same default the documents use when a deal has none.
 */
export function selectPublishedRateProductSources(
  rate: PublishedRate | null | undefined
): productMatching.StatedRateProductSources | undefined {
  if (!rate) {
    return undefined;
  }
  return {
    type: rate.productType,
    interestType: rate.type,
    // A published rate row carries no product compounding — see above.
    compoundFrequency: undefined,
    lenderId: rate.lenderId ?? undefined,
  };
}

/** {@link selectProductDisplayedRate} for a published rate row. */
export function selectPublishedRateDisplayedRate(
  rate: PublishedRate | null | undefined
): DisplayedRate | undefined {
  return selectProductDisplayedRate(selectPublishedRateProductSources(rate), rate?.minimumRate);
}

/** "5.28%" — or "—" when there is no rate. */
export function formatDisplayedRate(
  rate: DisplayedRate | null | undefined,
  options: { digits?: number } = {}
): string {
  return rate ? formatters.number.getPercentageFromDecimal(rate.rateDecimal, options) : "—";
}

/**
 * "5.29% compounded monthly" — the borrower/broker-facing form: the number and its compounding, no
 * jargon. Just "5.41%" when the rate is shown as stored.
 */
export function formatDisplayedRateWithCompounding(
  rate: DisplayedRate | null | undefined,
  options: { digits?: number } = {}
): string {
  const formatted = formatDisplayedRate(rate, options);
  return rate?.compounding ? `${formatted} compounded ${rate.compounding}` : formatted;
}

/** A premium as the website's chips show it: "+25bps" / "-10bps". */
export function formatPremiumBps(bps: number): string {
  return `${bps > 0 ? "+" : ""}${bps}bps`;
}

/** "compounded monthly", or undefined when the rate names no compounding. */
export function formatDisplayedRateCompounding(rate: DisplayedRate | null | undefined): string | undefined {
  return rate?.compounding ? `compounded ${rate.compounding}` : undefined;
}

/**
 * EMPLOYEE-facing wording with the governing effective rate alongside: "5.29% compounded monthly
 * (5.41% effective annual)". Never show this to borrowers or brokers — use
 * {@link formatDisplayedRateWithCompounding}.
 */
export function describeDisplayedRateInternal(rate: DisplayedRate | null | undefined): string {
  if (!rate) {
    return "";
  }
  const stated = formatDisplayedRateWithCompounding(rate);
  if (!rate.compounding) {
    return stated;
  }
  return `${stated} (${formatters.number.getPercentageFromDecimal(rate.effectiveRateDecimal)} effective annual)`;
}
